import { useState } from "react"
import { Card, CardContent, CardHeader } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import { Progress } from "@/components/ui/progress"
import { ChevronRight, ChevronLeft, Crown } from "lucide-react"

const onboardingSteps = [
  {
    id: 1,
    title: "Welcome to Apex",
    subtitle: "An exclusive community for those who shape their world through movement",
    type: "welcome"
  },
  {
    id: 2,
    title: "Tell us about yourself",
    subtitle: "Help us personalize your Apex experience",
    type: "personal"
  },
  {
    id: 3,
    title: "Your Movement Practice", 
    subtitle: "What drives your daily motion?",
    type: "movement"
  },
  {
    id: 4,
    title: "Style Preferences",
    subtitle: "Which Gameshape collections speak to you?",
    type: "style"
  },
  {
    id: 5,
    title: "Welcome to the Club",
    subtitle: "You're now an Apex Initiate. Your journey begins.",
    type: "complete"
  }
]

const movementTypes = [
  { id: "urban", label: "Urban Exploration", icon: "🏙️", desc: "Streets, stairs, city landscapes" },
  { id: "studio", label: "Studio Training", icon: "💪", desc: "Focused strength & conditioning" },
  { id: "outdoor", label: "Outdoor Adventures", icon: "🏃‍♂️", desc: "Running, hiking, cycling" },
  { id: "mindful", label: "Mindful Movement", icon: "🧘‍♀️", desc: "Yoga, pilates, flow states" },
  { id: "travel", label: "Travel & Experience", icon: "✈️", desc: "Movement across cultures" }
]

const stylePreferences = [
  { id: "architect", name: "Architect Collection", desc: "Structured. Purposeful. Refined.", color: "bg-tier-architect" },
  { id: "kinetic", name: "Kinetic Series", desc: "Dynamic. Performance. Flow.", color: "bg-accent" },
  { id: "form", name: "Form Essentials", desc: "Clean. Minimal. Foundational.", color: "bg-muted" }
]

export function ApexOnboarding() {
  const [currentStep, setCurrentStep] = useState(1)
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    movement: [] as string[],
    style: [] as string[]
  })

  const progress = (currentStep / onboardingSteps.length) * 100

  const handleNext = () => {
    if (currentStep < onboardingSteps.length) {
      setCurrentStep(currentStep + 1)
    }
  }

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1)
    }
  }

  const toggleMovement = (id: string) => {
    setFormData(prev => ({
      ...prev,
      movement: prev.movement.includes(id)
        ? prev.movement.filter(m => m !== id)
        : [...prev.movement, id]
    }))
  }

  const toggleStyle = (id: string) => {
    setFormData(prev => ({
      ...prev,
      style: prev.style.includes(id)
        ? prev.style.filter(s => s !== id)
        : [...prev.style, id]
    }))
  }

  const renderStepContent = () => {
    const step = onboardingSteps.find(s => s.id === currentStep)
    if (!step) return null

    switch (step.type) {
      case "welcome":
        return (
          <div className="text-center space-y-8 py-8">
            <div className="w-24 h-24 mx-auto rounded-full bg-apex-gradient flex items-center justify-center">
              <Crown size={40} className="text-black" />
            </div>
            <div className="space-y-4">
              <h1 className="text-3xl font-bold text-foreground">Welcome to Apex</h1>
              <p className="text-muted-foreground leading-relaxed max-w-sm mx-auto">
                You've been invited to join an exclusive community where movement meets luxury, 
                and every step shapes your story.
              </p>
            </div>
            <div className="space-y-3">
              <Badge className="bg-primary text-primary-foreground">
                Exclusive Community
              </Badge>
              <Badge className="bg-accent text-accent-foreground">
                Luxury Commerce
              </Badge>
              <Badge className="bg-secondary text-secondary-foreground">
                Style Challenges
              </Badge>
            </div>
          </div>
        )

      case "personal":
        return (
          <div className="space-y-6 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Your Name</label>
              <Input
                placeholder="Enter your full name"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className="bg-input border-border"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground">Email</label>
              <Input
                type="email"
                placeholder="your@email.com"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                className="bg-input border-border"
              />
            </div>
            <div className="p-4 rounded-lg bg-muted/20 border border-border/50">
              <p className="text-sm text-muted-foreground">
                <strong>Privacy:</strong> Your information is encrypted and used solely to 
                personalize your Apex experience. No spam, ever.
              </p>
            </div>
          </div>
        )

      case "movement":
        return (
          <div className="space-y-6 py-4">
            <div className="text-center space-y-2">
              <p className="text-muted-foreground">
                Select all that resonate with your movement practice:
              </p>
            </div>
            <div className="space-y-3">
              {movementTypes.map(type => (
                <button
                  key={type.id}
                  onClick={() => toggleMovement(type.id)}
                  className={`
                    w-full p-4 rounded-lg text-left transition-all duration-200 border-2
                    ${formData.movement.includes(type.id)
                      ? 'bg-primary/10 border-primary text-primary' 
                      : 'bg-muted/20 border-border hover:border-muted-foreground/30'
                    }
                  `}
                >
                  <div className="flex items-center gap-4">
                    <div className="text-2xl">{type.icon}</div>
                    <div className="flex-1">
                      <h4 className="font-medium">{type.label}</h4>
                      <p className="text-sm opacity-70">{type.desc}</p>
                    </div>
                    {formData.movement.includes(type.id) && (
                      <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center">
                        <span className="text-black text-sm">✓</span>
                      </div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )

      case "style":
        return (
          <div className="space-y-6 py-4">
            <div className="text-center space-y-2">
              <p className="text-muted-foreground">
                Which Gameshape collections align with your aesthetic?
              </p>
            </div>
            <div className="space-y-3">
              {stylePreferences.map(style => (
                <button
                  key={style.id}
                  onClick={() => toggleStyle(style.id)}
                  className={`
                    w-full p-6 rounded-lg text-left transition-all duration-200 border-2
                    ${formData.style.includes(style.id)
                      ? 'bg-primary/10 border-primary' 
                      : 'bg-muted/20 border-border hover:border-muted-foreground/30'
                    }
                  `}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-full ${style.color}`}></div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-foreground">{style.name}</h4>
                      <p className="text-sm text-muted-foreground">{style.desc}</p>
                    </div>
                    {formData.style.includes(style.id) && (
                      <div className="w-6 h-6 rounded-full bg-primary flex items-center justify-center">
                        <span className="text-black text-sm">✓</span>
                      </div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )

      case "complete":
        return (
          <div className="text-center space-y-8 py-8">
            <div className="w-24 h-24 mx-auto rounded-full bg-tier-initiate/20 flex items-center justify-center border-2 border-tier-initiate">
              <span className="text-2xl">◦</span>
            </div>
            <div className="space-y-4">
              <h1 className="text-3xl font-bold text-foreground">Welcome to Apex, {formData.name}</h1>
              <div className="space-y-2">
                <Badge className="tier-badge-initiate">
                  ◦ Initiate Member
                </Badge>
                <p className="text-muted-foreground leading-relaxed max-w-sm mx-auto">
                  You start your journey as an Initiate. Earn Apex Points through purchases, 
                  challenges, and community engagement to unlock Creator and Architect tiers.
                </p>
              </div>
            </div>
            <div className="space-y-4">
              <div className="p-4 rounded-lg bg-card-gradient border border-border">
                <h4 className="font-medium text-foreground mb-2">Your Starting Benefits:</h4>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• Access to exclusive Apex content</li>
                  <li>• Style challenge participation</li>
                  <li>• Community features</li>
                  <li>• Movement tracking & points</li>
                </ul>
              </div>
            </div>
          </div>
        )

      default:
        return null
    }
  }

  const isStepComplete = () => {
    const step = onboardingSteps.find(s => s.id === currentStep)
    if (!step) return false

    switch (step.type) {
      case "welcome":
      case "complete":
        return true
      case "personal":
        return formData.name.trim() && formData.email.trim()
      case "movement":
        return formData.movement.length > 0
      case "style":
        return formData.style.length > 0
      default:
        return false
    }
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-md mx-auto">
        {/* Progress */}
        <div className="sticky top-0 z-40 bg-background/80 backdrop-blur-lg border-b border-border">
          <div className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-muted-foreground">
                Step {currentStep} of {onboardingSteps.length}
              </span>
              <span className="text-sm font-medium text-primary">
                {Math.round(progress)}% Complete
              </span>
            </div>
            <Progress value={progress} className="h-1" />
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          <Card className="bg-card-gradient border-0">
            <CardHeader className="text-center">
              <h2 className="text-xl font-bold text-foreground">
                {onboardingSteps.find(s => s.id === currentStep)?.title}
              </h2>
              <p className="text-muted-foreground">
                {onboardingSteps.find(s => s.id === currentStep)?.subtitle}
              </p>
            </CardHeader>
            <CardContent>
              {renderStepContent()}
            </CardContent>
          </Card>

          {/* Navigation */}
          <div className="flex items-center justify-between mt-6">
            <Button
              variant="outline"
              onClick={handleBack}
              disabled={currentStep === 1}
              className="flex items-center gap-2"
            >
              <ChevronLeft size={16} />
              Back
            </Button>

            <Button
              onClick={handleNext}
              disabled={!isStepComplete()}
              className={`
                flex items-center gap-2
                ${currentStep === onboardingSteps.length 
                  ? 'bg-accent hover:bg-accent/90 text-accent-foreground' 
                  : 'bg-primary hover:bg-primary/90 text-primary-foreground'
                }
              `}
            >
              {currentStep === onboardingSteps.length ? (
                "Enter Apex"
              ) : (
                <>
                  Next
                  <ChevronRight size={16} />
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}